export enum SamplingMode {
  SINGLE_PRODUCT = 'SINGLE_PRODUCT',
  MULTIPLE_PRODUCTS = 'MULTIPLE_PRODUCTS'
}

export enum ProductOptionRequirement {
  NONE = 'NONE',
  REQUIRED = 'REQUIRED'
}

export enum ProductOptionSource {
  MANUAL = 'MANUAL',
  PRODUCT_PAGE = 'PRODUCT_PAGE'
}

export interface SamplingImage {
  src: string;
  fileName?: string;
  type?: 'image' | 'video';
}

export interface SamplingOption {
  id: string;
  name: string;
  allowedValues: string[];
  selectionGuide?: string;
  selectionGuideFormat?: 'text' | 'html';
  selectionGuideImages: SamplingImage[];
}

export interface SamplingProduct {
  id: string;
  serverProductId?: string;
  name: string;
  imageUrl: string;
  imageFileName?: string;
  productUrl: string;
  retailPriceUsd: number | null;
  optionRequirement: ProductOptionRequirement;
  optionSource?: ProductOptionSource;
  options: SamplingOption[];
  productPageSelectionGuide?: string;
  productPageSelectionGuideFormat?: 'text' | 'html';
  productPageSelectionGuideImages?: SamplingImage[];
  brandName?: string;
  brandLogoUrl?: string;
}

export interface PhysicalSamplingConfig {
  mode: SamplingMode;
  maxSelectableProducts: number;
  products: SamplingProduct[];
  isLegacy?: boolean;
  /** Unknown service configurations remain readable but must never be rewritten. */
  isUnsupported?: boolean;
}

export type SamplingBrand = Pick<SamplingProduct, 'brandName' | 'brandLogoUrl'>;

export interface ProductOptionSelection {
  optionId?: string;
  optionName: string;
  value: string;
}

export interface SelectedProductSnapshot {
  productId: string;
  productSnapshot?: SamplingProduct;
  optionSelections: ProductOptionSelection[];
  productPageSelection?: string;
}

export interface ProductSelectionSnapshot {
  selectedAt?: string;
  products: SelectedProductSnapshot[];
}

export interface ProductSelectionDraftValue {
  optionSelections: Record<string, string>;
  productPageSelection: string;
}

export interface ProductSelectionDraft {
  selectedProductIds: string[];
  valuesByProductId: Record<string, ProductSelectionDraftValue>;
}

export interface ProductSelectionValidationIssue {
  code:
    | 'UNSUPPORTED_CONFIG'
    | 'INVALID_SELECTION_COUNT'
    | 'UNKNOWN_PRODUCT'
    | 'REQUIRED_OPTION'
    | 'INVALID_OPTION_VALUE'
    | 'REQUIRED_PRODUCT_PAGE_SELECTION';
  productId?: string;
  optionId?: string;
}

/** Supplied by an API adapter only once the new selection contract is available. */
export type ProductSelectionSubmit = (
  selection: ProductSelectionSnapshot
) => Promise<void>;

/** Explicit service value; no client calculation of the highest priced combination. */
export interface SamplingValueSummary {
  amountUsd: number;
  isUpperBound: boolean;
}
